import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

/**
 * QUIÉN ESTÁ OCUPADO A ESA HORA.
 *
 * Se programaba sin mirar si el técnico ya tenía algo: quedaron tres órdenes
 * de Luis Alfonso pisándose la misma tarde, aceptadas sin una advertencia. Un
 * cronograma que dice que alguien está en dos sitios no sirve para nada.
 *
 * La base ya lo impide (0209). Esto es para que la pantalla lo enseñe ANTES
 * de escoger: enterarse al guardar, después de llenar el formulario, es la
 * peor forma de enterarse.
 */
export type Ocupacion = { numero: string; desde: string; hasta: string }

export function useOcupados(
  inicioISO: string | null,
  horas: number,
  excluirOrdenId?: string | null,
) {
  // El fin aproximado: la base calcula el suyo sobre el horario operativo,
  // pero para avisar en pantalla esto basta y evita otra llamada.
  const finISO =
    inicioISO && horas > 0
      ? new Date(new Date(inicioISO).getTime() + horas * 3600_000).toISOString()
      : null

  const consulta = useQuery({
    queryKey: ['ocupados', inicioISO, finISO, excluirOrdenId ?? null],
    enabled: Boolean(inicioISO && finISO),
    staleTime: 30_000,
    queryFn: async (): Promise<Record<string, Ocupacion>> => {
      const { data, error } = await supabase.rpc('fn_colaboradores_para_programar', {
        p_inicio: inicioISO!,
        p_fin: finISO!,
        p_excluir_orden_id: excluirOrdenId ?? null,
      })

      if (error) throw new Error(mensajeDeError(error))

      const porId: Record<string, Ocupacion> = {}
      for (const fila of (data ?? []) as unknown as Array<{
        id: string
        ocupado: boolean
        con_que_orden: string | null
        desde: string | null
        hasta: string | null
      }>) {
        if (fila.ocupado && fila.con_que_orden) {
          porId[fila.id] = {
            numero: fila.con_que_orden,
            desde: fila.desde ?? '',
            hasta: fila.hasta ?? '',
          }
        }
      }

      return porId
    },
  })

  return consulta.data ?? {}
}
